import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown } from 'lucide-react';
import './Eventsection.css';
import coldImg from '../assets/images/cold.png';
import arrowImg from '../assets/images/arrow.png';

const Eventsection = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [selectedcity, setSelectedcity] = useState('All');
    const [selectedType, setSelectedType] = useState('All');
    const [iscityOpen, setIscityOpen] = useState(false);
    const [isTypeOpen, setIsTypeOpen] = useState(false);
    const [isDateOpen, setIsDateOpen] = useState(false);
    const [range, setRange] = useState({ start: null, end: null });
    const [displayMonth, setDisplayMonth] = useState(new Date());
    const cityDropdownRef = useRef(null);
    const dateDropdownRef = useRef(null);
    const typeDropdownRef = useRef(null);

    const [events, setEvents] = useState([]);

    const cityOptions = useMemo(() => {
        const values = new Set();
        events.forEach((event) => {
            if (event.city) {
                values.add(event.city);
            }
        });
        return ['All', ...Array.from(values)];
    }, [events]);

    const nameOptions = useMemo(() => {
        const values = new Set();
        events.forEach((event) => {
            if (event.title) {
                values.add(event.title);
            }
        });
        return ['All', ...Array.from(values)];
    }, [events]);

    const makeEvent = (dateIso, title, city, type, overrides = {}) => {
        const eventDate = new Date(dateIso);
        return {
            id: overrides.id || null,
            dateIso,
            dateDay: `${eventDate.getDate()}`,
            dateMonth: eventDate.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
            title,
            city,
            type,
            time: overrides.time || "05:00 PM Onwards",
            description: overrides.description || "A thoughtfully designed singles mixer where conversations flow naturally - without pressure, expectations, or forced interactions.",
            imageUrl: overrides.imageUrl || coldImg
        };
    };

    useEffect(() => {
        let isMounted = true;
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        const loadEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/public/events`);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to load events');
                }

                const items = Array.isArray(data.events) ? data.events : [];
                const mapped = items.map((item) => {
                    const cityLabel = item.city
                        || [item.city, item.state, item.country].filter(Boolean).join(', ')
                        || 'city TBA';
                    const rawThumbnail = item.thumbnail || '';
                    const normalizedThumbnail = rawThumbnail
                        ? rawThumbnail.replace(/^https?:\/\/localhost(:\d+)?/i, API_BASE_URL)
                        : '';
                    return makeEvent(item.date, item.title || 'EVENT', cityLabel, item.type || 'All', {
                        id: item._id,
                        time: item.time || undefined,
                        description: item.shortDescription || item.description,
                        imageUrl: normalizedThumbnail || rawThumbnail
                    });
                });

                if (isMounted) {
                    setEvents(mapped);
                }
            } catch (error) {
                console.error('Failed to load events', error);
            }
        };

        loadEvents();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!iscityOpen && !isDateOpen && !isTypeOpen) {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            const target = event.target;
            const isInsidecity = cityDropdownRef.current && cityDropdownRef.current.contains(target);
            const isInsideDate = dateDropdownRef.current && dateDropdownRef.current.contains(target);
            const isInsideType = typeDropdownRef.current && typeDropdownRef.current.contains(target);

            if (!isInsidecity && !isInsideDate && !isInsideType) {
                setIscityOpen(false);
                setIsDateOpen(false);
                setIsTypeOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [iscityOpen, isDateOpen, isTypeOpen]);

    const formatReadableDate = (date) => {
        if (!date) return '';
        return date.toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatRangeLabel = () => {
        if (!range.start) return 'Date range';
        if (range.start && !range.end) return formatReadableDate(range.start);
        return `${formatReadableDate(range.start)} - ${formatReadableDate(range.end)}`;
    };

    const limitWords = (value, maxWords) => {
        if (!value) return '';
        const words = value.trim().split(/\s+/);
        if (words.length <= maxWords) return value;
        return `${words.slice(0, maxWords).join(' ')}...`;
    };

    const handleDateSelect = (selectedDate) => {
        if (!range.start || (range.start && range.end)) {
            setRange({ start: selectedDate, end: null });
            return;
        }
        if (selectedDate < range.start) {
            setRange({ start: selectedDate, end: range.start });
            return;
        }
        setRange({ start: range.start, end: selectedDate });
    };

    const getMonthMatrix = (year, month) => {
        const firstDay = new Date(year, month, 1);
        const startDay = firstDay.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells = [];

        for (let i = 0; i < startDay; i += 1) {
            cells.push(null);
        }
        for (let day = 1; day <= daysInMonth; day += 1) {
            cells.push(new Date(year, month, day));
        }
        return cells;
    };

    const applyPreset = (preset) => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

        if (preset === 'Today') {
            setRange({ start: today, end: today });
        } else if (preset === 'Yesterday') {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            setRange({ start: yesterday, end: yesterday });
        } else if (preset === 'Last 7 days') {
            const start = new Date(today);
            start.setDate(today.getDate() - 6);
            setRange({ start, end: today });
        } else if (preset === 'Last 30 days') {
            const start = new Date(today);
            start.setDate(today.getDate() - 29);
            setRange({ start, end: today });
        } else if (preset === 'This month') {
            setRange({ start: startOfMonth, end: endOfMonth });
        } else if (preset === 'Last month') {
            setRange({ start: lastMonthStart, end: lastMonthEnd });
        } else {
            setRange({ start: null, end: null });
        }
    };

    const filteredEvents = events.filter((event) => {
        const searchValue = searchText.trim().toLowerCase();
        const matchesSearch = !searchValue
            || event.title.replace(/\s+/g, ' ').toLowerCase().includes(searchValue)
            || event.city.toLowerCase().includes(searchValue);
        const matchescity = selectedcity === 'All' || event.city === selectedcity;
        const matchesType = selectedType === 'All' || event.title === selectedType;

        if (!range.start || !range.end) {
            return matchesSearch && matchescity && matchesType;
        }

        const eventDate = new Date(event.dateIso);
        const start = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
        const end = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate());
        const matchesDate = eventDate >= start && eventDate <= end;

        return matchesSearch && matchescity && matchesType && matchesDate;
    });

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthA = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1);
    const monthB = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1);
    const monthACells = getMonthMatrix(monthA.getFullYear(), monthA.getMonth());
    const monthBCells = getMonthMatrix(monthB.getFullYear(), monthB.getMonth());

    return (
        <div className="upcome-container">
            {/* Header Section */}
            <div className="header-section">
                <div className="upcoming-badge">Upcoming Events</div>
                <div className="header-description">
                    Discover thoughtfully curated social experiences designed for real connections, meaningful conversations, and memorable moments.

                    Whether you're joining us for a singles mixer or a group experience, every Event House gathering is designed with intention.
                </div>
            </div>

            {/* Filter Section */}
            <div className="filter-wrapper">
                <div className="filter-box" style={{ maxWidth: '280px' }}>
                    <input
                        type="text"
                        className="filter-input"
                        placeholder="Search experiences"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                    <Search className="filter-icon" size={20} />
                </div>

                <div className="filter-box" style={{ maxWidth: '180px' }} ref={cityDropdownRef}>
                    <button
                        type="button"
                        className="filter-dropdown"
                        onClick={() => {
                            setIscityOpen((prev) => !prev);
                            setIsTypeOpen(false);
                            setIsDateOpen(false);
                        }}
                    >
                        <span>{selectedcity === 'All' ? 'Search city' : selectedcity}</span>
                        <ChevronDown size={20} />
                    </button>
                    {iscityOpen && (
                        <div className="filter-menu">
                            {cityOptions.map((city) => (
                                <button
                                    key={city}
                                    type="button"
                                    className={`filter-option${selectedcity === city ? ' filter-option-active' : ''}`}
                                    onClick={() => {
                                        setSelectedcity(city);
                                        setIscityOpen(false);
                                    }}
                                >
                                    {city}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="filter-box" style={{ maxWidth: '240px' }} ref={dateDropdownRef}>
                    <button
                        type="button"
                        className="filter-dropdown"
                        onClick={() => {
                            setIsDateOpen((prev) => !prev);
                            setIscityOpen(false);
                            setIsTypeOpen(false);
                        }}
                    >
                        <span>{formatRangeLabel()}</span>
                        <ChevronDown size={20} />
                    </button>
                    {isDateOpen && (
                        <div className="date-picker">
                            <div className="date-sidebar">
                                <div className="date-sidebar-title">Recently used</div>
                                {['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'This month', 'Last month', 'Custom'].map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        className="date-preset"
                                        onClick={() => applyPreset(preset)}
                                    >
                                        {preset}
                                    </button>
                                ))}
                            </div>

                            <div className="date-main">
                                <div className="date-header">
                                    <button
                                        type="button"
                                        className="date-nav"
                                        onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1))}
                                    >
                                        &#8249;
                                    </button>
                                    <div className="date-months">
                                        <div className="date-month-label">{monthNames[monthA.getMonth()]} {monthA.getFullYear()}</div>
                                        <div className="date-month-label">{monthNames[monthB.getMonth()]} {monthB.getFullYear()}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="date-nav"
                                        onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1))}
                                    >
                                        &#8250;
                                    </button>
                                </div>

                                <div className="date-calendars">
                                    {[{ month: monthA, cells: monthACells }, { month: monthB, cells: monthBCells }].map((monthData) => (
                                        <div key={monthData.month.toISOString()} className="calendar">
                                            <div className="calendar-weekdays">
                                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                                    <span key={day}>{day}</span>
                                                ))}
                                            </div>
                                            <div className="calendar-grid">
                                                {monthData.cells.map((date, index) => {
                                                    if (!date) {
                                                        return <span key={`${monthData.month.toISOString()}-empty-${index}`} className="calendar-empty" />;
                                                    }
                                                    const isStart = range.start && date.toDateString() === range.start.toDateString();
                                                    const isEnd = range.end && date.toDateString() === range.end.toDateString();
                                                    const isBetween = range.start && range.end && date > range.start && date < range.end;
                                                    return (
                                                        <button
                                                            key={date.toISOString()}
                                                            type="button"
                                                            className={`calendar-day${isStart || isEnd ? ' calendar-day-active' : ''}${isBetween ? ' calendar-day-range' : ''}`}
                                                            onClick={() => handleDateSelect(date)}
                                                        >
                                                            {date.getDate()}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="date-footer">
                                    <div className="date-fields">
                                        <select className="date-custom">
                                            <option>Custom</option>
                                        </select>
                                        <input
                                            type="text"
                                            readOnly
                                            className="date-input"
                                            value={range.start ? formatReadableDate(range.start) : ''}
                                            placeholder="Start date"
                                        />
                                        <input
                                            type="text"
                                            readOnly
                                            className="date-input"
                                            value={range.end ? formatReadableDate(range.end) : ''}
                                            placeholder="End date"
                                        />
                                    </div>
                                    <div className="date-actions">
                                        <button
                                            type="button"
                                            className="date-cancel"
                                            onClick={() => {
                                                setRange({ start: null, end: null });
                                                setIsDateOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="date-apply"
                                            onClick={() => setIsDateOpen(false)}
                                        >
                                            Update
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="filter-box" style={{ maxWidth: '200px' }} ref={typeDropdownRef}>
                    <button
                        type="button"
                        className="filter-dropdown"
                        onClick={() => {
                            setIsTypeOpen((prev) => !prev);
                            setIscityOpen(false);
                            setIsDateOpen(false);
                        }}
                    >
                        <span>{selectedType === 'All' ? 'Events name' : selectedType}</span>
                        <ChevronDown size={20} />
                    </button>
                    {isTypeOpen && (
                        <div className="filter-menu">
                            {nameOptions.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`filter-option${selectedType === type ? ' filter-option-active' : ''}`}
                                    onClick={() => {
                                        setSelectedType(type);
                                        setIsTypeOpen(false);
                                    }}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Events Grid */}
            <div className="events-grrid">
                {filteredEvents.map((event, index) => (
                    <div
                        key={index}
                        className="event-cart"
                        onClick={() => navigate(`/Singleeventdetails/${event.id || ''}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                navigate(`/Singleeventdetails/${event.id || ''}`);
                            }
                        }}
                    >
                        <div className="card-header">
                            <div style={{ textAlign: "center", marginTop: "15px" }}>
                                <div className="date-container">
                                    <span className="date-day">{event.dateDay}</span>
                                    <span className="date-month">{event.dateMonth}</span>
                                </div>
                                <h3 className="card-tit">{event.title}</h3>
                            </div>
                            <div className="card-image-wrapper">
                                <img src={event.imageUrl} alt="Event" className="card-imag" />

                            </div>
                        </div>
                        <div className="meta-tag">
                            <span style={{ fontWeight: 600 }}>
                                {limitWords(event.city, 3)}
                            </span> | {event.time}
                        </div>
                        <p className="event-desc">{event.description}</p>

                        <div className="arrow-icon">
                            <img src={arrowImg} alt="" className="arrow-image" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Eventsection;
